import APIService from './APIService';
import { EventBatchRequest, EventItem } from '../store/generated/events';
import { EventType, EVENT_SCHEMAS } from '../types/events';
import { z } from 'zod';

/**
 * Service for handling event tracking with efficient batching and sending
 */
class TrackingService {
  // Queue events to batch send them
  private static eventQueue: EventItem[] = [];
  private static flushTimeout: NodeJS.Timeout | null = null;
  private static readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private static readonly MAX_BATCH_SIZE = 50; // Max events per batch

  /**
   * Track an event with validation
   * - Adds it to the dataLayer for GA tracking
   * - Queues it for server-side tracking
   *
   * @param eventType The type of event
   * @param eventData Additional data for the event
   * @returns boolean indicating if validation passed
   */
  static trackEvent(eventType: EventType | string, eventData: Record<string, any> = {}): boolean {
    // Validate event data if schema exists for this event type
    if (eventType in EVENT_SCHEMAS) {
      const schema = EVENT_SCHEMAS[eventType as EventType];
      try {
        // Parse and validate the event data
        schema.parse(eventData);
      } catch (error) {
        if (error instanceof z.ZodError) {
          console.error(`Invalid event data for ${eventType}:`, error.format());
          // Log validation errors but don't send invalid events
          return false;
        }
      }
    }

    // Queue for server-side batch processing
    this.eventQueue.push({
      event_type: eventType,
      event_data: eventData,
    });

    // Set up flush if not already scheduled
    if (!this.flushTimeout) {
      this.flushTimeout = setTimeout(() => this.flushEvents(), this.FLUSH_INTERVAL);
    }

    // Immediately flush if we've reached max batch size
    if (this.eventQueue.length >= this.MAX_BATCH_SIZE) {
      if (this.flushTimeout) {
        clearTimeout(this.flushTimeout);
        this.flushTimeout = null;
      }
      this.flushEvents();
    }

    return true;
  }

  /**
   * Send batched events to server
   * This is called automatically on a timer or when batch size is reached
   * Can also be called manually to force sending events immediately
   */
  static async flushEvents() {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];
    this.flushTimeout = null;

    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await APIService.makeAPIPostRequest({
        requestString: '/v2/provider/events',
        accessToken,
        body: { events } as EventBatchRequest,
      });

      // If there were failed events, log them but don't retry for analytics
      if (response.ok && response.value.failed_events?.length) {
        console.warn('Some events failed to send:', response.value.failed_events);
      } else if (!response.ok) {
        console.error('Failed to send events batch:', response.error);
      }
    } catch (error) {
      console.error('Error sending tracking events:', error);
      // We don't retry events as they're generally non-critical
    }
  }

  /**
   * Force flush all queued events, useful when user is navigating away
   * Call this in component unmount/cleanup or app shutdown
   */
  static forceFlush() {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
    return this.flushEvents();
  }
}

export default TrackingService;
